// src/utils/cache/spots-cache.js
// Spots batch loading and caching
// Single source of truth for spots caching and batch loading

import { LOGGING } from '/settings.js';
import { fetchSpotsByIds, fetchSingleSpot } from '../spots-ids.js';
import { createBatchLoader, createSortedBatchKey } from './cache-helpers.js';

/**
 * Spots Cache - In-memory cache for spot data
 * Maps spotId -> spot object
 */
const spotsCache = new Map();

/**
 * In-flight batch requests - tracks ongoing batch loads to prevent duplicate calls
 * Maps batchKey (sorted spot IDs joined by comma) -> Promise
 */
const inFlightBatchRequests = new Map();

// Create batch loader using shared helper
const batchLoadSpotsImpl = createBatchLoader({
    cache: spotsCache,
    inFlightRequests: inFlightBatchRequests,
    getCacheKey: (spotId, options = {}) => {
        // Include map_id in cache key if provided, so spots with map-specific tags are cached separately
        const spotIdNum = Number(spotId);
        if (options && options.map_id) {
            return `spot_${spotIdNum}_map_${options.map_id}`;
        }
        return spotIdNum;
    },
    getBatchKey: (batch, options = {}) => {
        const baseKey = createSortedBatchKey(batch);
        // Include map_id in batch key if provided, so different map_ids create different batches
        if (options && options.map_id) {
            return `${baseKey}_map_${options.map_id}`;
        }
        return baseKey;
    },
    filterUncached: (spotIds, cache, getCacheKey, ...args) => {
        // Normalize spot IDs to numbers first
        const normalizedIds = spotIds.map(id => Number(id)).filter(id => id > 0);
        const options = args[0] || {};
        return normalizedIds.filter(id => !cache.has(getCacheKey(id, options)));
    },
    fetchBatch: async (batch, options = {}) => {
        return await fetchSpotsByIds(batch.map(id => String(id)), options);
    },
    processResponse: (spots, batch, cache, getCacheKey, allResults, getDefaultValue, ...args) => {
        const options = args[0] || {};
        // Store in cache
        if (Array.isArray(spots)) {
            spots.forEach(spot => {
                if (spot && spot.id) {
                    const spotId = Number(spot.id);
                    const key = getCacheKey(spotId, options);
                    cache.set(key, spot);
                    if (LOGGING.ENABLED && LOGGING.CACHE) {
                        console.log(`[Spots Cache] Populated cache for spot ${spotId} with key ${key}`);
                    }
                    // Store in allResults keyed by spot ID (not cache key) for compatibility with callers
                    allResults.set(spotId, spot);
                }
            });
        }

        // Cache null for spots that were not found (to avoid re-requesting)
        batch.forEach(spotId => {
            const spotIdNum = Number(spotId);
            const key = getCacheKey(spotIdNum, options);
            if (!cache.has(key)) {
                cache.set(key, null);
                // Don't set null in allResults - only set actual spots
            }
        });
    },
    getCached: (spotIds, cache, getCacheKey, ...args) => {
        const results = new Map();
        const options = args[0] || {};
        spotIds.forEach(id => {
            const spotId = Number(id);
            const key = getCacheKey(spotId, options);
            if (cache.has(key)) {
                const spot = cache.get(key);
                // Only return non-null spots (null means not found or failed)
                // Return Map keyed by spot ID (not cache key) for compatibility with callers
                if (spot !== null) {
                    results.set(spotId, spot);
                }
            }
        });
        return results;
    },
    getDefaultValue: () => null, // null for spots that are not found
    cacheName: 'Spots Cache',
    maxBatchSize: 50
});

/**
 * Batch load spots for multiple IDs in a single API call
 * @param {Array<number|string>} spotIds - Array of spot IDs
 * @param {Object} options - Options for fetching (lang, etc.)
 * @returns {Promise<Map<number, Object>>} Map of spotId -> spot object
 */
export async function batchLoadSpots(spotIds, options = {}) {
    // Normalize spot IDs first
    const normalizedIds = spotIds.map(id => Number(id)).filter(id => id > 0);
    if (normalizedIds.length === 0) {
        return new Map();
    }
    return await batchLoadSpotsImpl(normalizedIds, options);
}

/**
 * Get cached spots for spot IDs
 * @param {Array<number>} spotIds - Array of spot IDs
 * @returns {Map<number, Object>} Map of spotId -> spot object
 */
export function getCachedSpots(spotIds) {
    const results = new Map();
    spotIds.forEach(id => {
        const spotId = Number(id);
        if (spotsCache.has(spotId)) {
            const spot = spotsCache.get(spotId);
            // Only return non-null spots (null means not found or failed)
            if (spot !== null) {
                results.set(spotId, spot);
            }
        }
    });
    return results;
}

/**
 * Get a single spot (from cache or fetch if not cached)
 * @param {number|string} spotId - Spot ID
 * @param {Object} options - Options for fetching (lang, etc.)
 * @returns {Promise<Object|null>} Spot object or null if not found
 */
export async function getSpot(spotId, options = {}) {
    const spotIdNum = Number(spotId);

    // Use same cache key logic as batch loader
    const getCacheKey = (id, opts = {}) => {
        if (opts && opts.map_id) {
            return `spot_${id}_map_${opts.map_id}`;
        }
        return id;
    };
    const cacheKey = getCacheKey(spotIdNum, options);

    // Check cache first
    if (spotsCache.has(cacheKey)) {
        const spot = spotsCache.get(cacheKey);
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.log(`[Spots Cache] Cache HIT for spot ${spotId} (key: ${cacheKey})`);
        }
        // If caller requires images but cached spot has none, fetch full spot and refresh cache
        if (options.requireImages && spot && (!Array.isArray(spot.images) || spot.images.length === 0)) {
            try {
                const refreshed = await fetchSingleSpot(spotIdNum, { lang: options.lang, map_id: options.map_id, areaSlug: options.areaSlug });
                if (refreshed) {
                    spotsCache.set(cacheKey, refreshed);
                    if (LOGGING.ENABLED && LOGGING.CACHE) {
                        console.log(`[Spots Cache] Refreshed spot ${spotId} with images (key: ${cacheKey})`);
                    }
                    return refreshed;
                }
            } catch (e) {
                console.warn('[Spots Cache] Failed to refresh spot with images:', e);
            }
        }
        return spot; // Can be null if spot was not found
    } else {
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.warn(`[Spots Cache] Cache MISS for spot ${spotId} (key: ${cacheKey})`);
        }
    }

    // Check if this spot is part of an in-flight batch request
    // Look through in-flight requests to see if any batch contains this spot
    // Note: batch keys include map_id, so we need to check both with and without map_id
    for (const [batchKey, promise] of inFlightBatchRequests.entries()) {
        // Extract spot IDs from batch key (format: "1,2,3" or "1,2,3_map_5")
        const baseBatchKey = batchKey.split('_map_')[0];
        const batchIds = baseBatchKey.split(',').map(id => Number(id));
        if (batchIds.includes(spotIdNum)) {
            if (LOGGING.ENABLED && LOGGING.CACHE) {
                console.log(`[Spots Cache] Spot ${spotId} is in in-flight batch, waiting...`);
            }
            // Wait for the batch to complete
            await promise;
            // Check cache again after batch completes
            if (spotsCache.has(cacheKey)) {
                return spotsCache.get(cacheKey);
            }
        }
    }

    // Not in cache and not in-flight, batch load (even if just one spot, uses same endpoint)
    const results = await batchLoadSpots([spotIdNum], options);
    // Results Map is keyed by spot ID (not cache key)
    return results.get(spotIdNum) || null;
}

/**
 * Preload spots (batch load in background)
 * This is called after spots are displayed to preload spot data
 * @param {Array<number|string>} spotIds - Array of spot IDs
 * @param {Object} options - Options for fetching (lang, etc.)
 * @returns {Promise<void>} Resolves when spots are loaded (or failed)
 */
export async function preloadSpots(spotIds, options = {}) {
    if (!spotIds || !Array.isArray(spotIds) || spotIds.length === 0) {
        return;
    }

    // Don't await - load in background
    batchLoadSpots(spotIds, options).catch(error => {
        console.warn('[Spots Cache] Error preloading spots:', error);
    });
}

/**
 * Clear spots cache for specific spots
 * @param {Array<number>} spotIds - Array of spot IDs to clear (optional, clears all if not provided)
 */
export function clearSpotsCache(spotIds = null) {
    if (spotIds && Array.isArray(spotIds)) {
        spotIds.forEach(id => spotsCache.delete(Number(id)));
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.log(`[Spots Cache] Cleared cache for ${spotIds.length} spots`);
        }
    } else {
        spotsCache.clear();
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.log('[Spots Cache] Cleared all spots cache');
        }
    }
}

/**
 * Check if a spot is cached
 * @param {number|string} spotId - Spot ID
 * @returns {boolean} True if spot is cached (even if null)
 */
export function hasCachedSpot(spotId) {
    return spotsCache.has(Number(spotId));
}

